// Compliance client — legal documents + acceptances via compliance-service.
import { gatewayApi } from '@/lib/hrApi';

export interface RequiredDoc {
  doc_type: string;
  title: string;
  version: number;
}

export interface LegalDocVersion {
  doc_type: string;
  version: number;
  language: string;
  title: string;
  body: string;
  effective_date?: string;
}

// Documents the current user still needs to accept (current version not yet
// accepted) — drives the first-login / re-acceptance gate.
export const getRequired = () =>
  gatewayApi
    .get<{ required: RequiredDoc[] | null }>('/api/v1/compliance/required')
    .then((r) => r.data.required ?? []);

export const getDocument = (docType: string, lang = 'en') =>
  gatewayApi
    .get<LegalDocVersion>(`/api/v1/compliance/legal-documents/${docType}`, { params: { lang } })
    .then((r) => r.data);

export const acceptDocument = (docType: string, version: number, source = 'first_login') =>
  gatewayApi
    .post('/api/v1/compliance/acceptances', { doc_type: docType, version, source })
    .then((r) => r.data);

// --- Company-admin compliance monitoring (privileged roles) ---------------

export interface TenantAcceptance {
  id: string;
  user_id?: string | null;
  user_role?: string;
  doc_type: string;
  version: number;
  platform?: string;
  source?: string;
  accepted_at: string;
}

export interface ComplianceSummaryRow {
  doc_type: string;
  title: string;
  version: number;
  accepted_count: number;
}

// Every acceptance in the caller's tenant (admin/HR/manager only — server-enforced).
export const getTenantAcceptances = () =>
  gatewayApi
    .get<{ acceptances: TenantAcceptance[] | null }>('/api/v1/compliance/acceptances')
    .then((r) => r.data.acceptances ?? []);

// Per-document acceptance roll-up for the caller's tenant.
export const getTenantSummary = () =>
  gatewayApi
    .get<{ summary: ComplianceSummaryRow[] | null }>('/api/v1/compliance/acceptances/summary')
    .then((r) => r.data.summary ?? []);

// --- Consent management (self-service, any authenticated user) ------------

export interface ConsentState {
  kind: string;
  label: string;
  description: string;
  essential: boolean;
  granted: boolean;
  explicit: boolean;
  source?: string;
  updated_at?: string;
  default_granted: boolean;
}

export const getConsents = () =>
  gatewayApi
    .get<{ consents: ConsentState[] | null }>('/api/v1/compliance/consents')
    .then((r) => r.data.consents ?? []);

export const updateConsents = (consents: Array<{ kind: string; granted: boolean }>) =>
  gatewayApi
    .put<{ consents: ConsentState[] | null }>('/api/v1/compliance/consents', { consents })
    .then((r) => r.data.consents ?? []);

// --- Data-subject (DSAR) requests — self-service ---------------------------

export interface PrivacyRequest {
  id: string;
  request_type: string;
  status: string;
  details?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export const getMyPrivacyRequests = () =>
  gatewayApi
    .get<{ requests: PrivacyRequest[] | null }>('/api/v1/compliance/privacy-requests/me')
    .then((r) => r.data.requests ?? []);

export const submitPrivacyRequest = (requestType: string, note?: string) =>
  gatewayApi
    .post<PrivacyRequest>('/api/v1/compliance/privacy-requests', {
      request_type: requestType,
      details: note ? { note } : {},
    })
    .then((r) => r.data);

// Download the caller's own completed data export (JSON) and save it.
export const downloadMyExport = async (id: string) => {
  const res = await gatewayApi.get(`/api/v1/compliance/privacy-requests/me/${id}/export`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `my-data-${id}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

// --- Trust Center (marketplace M1) ---
export interface DataSharingGrant {
  id: string;
  partner_id: string;
  product_id?: string | null;
  purpose: string;
  scopes: string[];
  version: number;
  granted_at: string;
  withdrawn_at?: string | null;
}
export interface DataAccessEntry {
  id: string;
  accessor_type: string;
  accessor_id: string;
  accessor_name: string;
  purpose: string;
  context: string;
  occurred_at: string;
}
export const getDataSharingGrants = () =>
  gatewayApi.get<{ grants: DataSharingGrant[] }>('/api/v1/compliance/data-sharing-grants').then((r) => r.data.grants ?? []);
export const withdrawDataSharingGrant = (id: string) =>
  gatewayApi.delete(`/api/v1/compliance/data-sharing-grants/${id}`).then((r) => r.data);
export const getMyDataAccess = () =>
  gatewayApi.get<{ entries: DataAccessEntry[] }>('/api/v1/compliance/me/data-access').then((r) => r.data.entries ?? []);
